import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, extractAxiosError } from "@/lib/api";
import { Dialog } from "@/components/EditPercentagesDialog";
import { toast } from "sonner";

function formatWhen(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch (_e) {
        return iso;
    }
}

function ProfileSection({ title, children, empty = false, emptyText = "None recorded." }) {
    return (
        <section className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
            <p className="label-eyebrow mb-3">{title}</p>
            {empty ? <p className="text-xs text-white/40">{emptyText}</p> : children}
        </section>
    );
}

function BankSummary({ bank }) {
    if (!bank?.account_last4) {
        return <span className="text-xs text-white/35">No bank on file</span>;
    }
    return (
        <span className="text-xs font-mono text-white/65">
            {bank.bank_name || "Bank"} · acct ···{bank.account_last4}
            {(bank.branch_last4 || bank.routing_last4)
                ? ` · ${bank.branch_label || "Branch"} ···${bank.branch_last4 || bank.routing_last4}`
                : ""}
            {bank.verified ? " · verified" : ""}
        </span>
    );
}

function LoadingBody() {
    return (
        <div className="grid min-h-[12rem] place-items-center gap-2 text-sm text-white/45">
            <Loader2 className="h-5 w-5 animate-spin admin-accent-icon" />
            Loading profile…
        </div>
    );
}

export function AdminUserProfileDialog({ userId, open, onClose }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/users/${userId}/profile`);
            setProfile(data);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load user profile"));
            onClose?.();
        } finally {
            setLoading(false);
        }
    }, [userId, onClose]);

    useEffect(() => {
        if (open && userId) {
            setProfile(null);
            load();
        }
    }, [open, userId, load]);

    if (!open) return null;

    const user = profile?.user;

    return (
        <Dialog
            onClose={onClose}
            title={user ? user.name || user.email : "User profile"}
            subtitle={user ? `${user.email} · ${user.role || "user"} · ${user.plan || "free"} plan` : "Full platform dossier"}
            testid="admin-user-profile-dialog"
            size="profile"
        >
            {loading || !profile ? (
                <LoadingBody />
            ) : (
                <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
                    <ProfileSection title="Account">
                        <dl className="grid gap-2 text-xs sm:grid-cols-2">
                            <div><dt className="text-white/40">User ID</dt><dd className="font-mono text-white/75">{user.id}</dd></div>
                            <div><dt className="text-white/40">Created</dt><dd className="text-white/75">{formatWhen(user.created_at)}</dd></div>
                            <div><dt className="text-white/40">2FA</dt><dd className="text-white/75">{user.totp_enabled ? "Enabled" : "Off"}</dd></div>
                            <div><dt className="text-white/40">Password reset required</dt><dd className="text-white/75">{user.password_reset_required ? "Yes" : "No"}</dd></div>
                        </dl>
                    </ProfileSection>

                    <ProfileSection title="Organizations & memberships" empty={!profile.memberships?.length && !profile.owned_organizations?.length}>
                        {profile.owned_organizations?.length > 0 && (
                            <div className="mb-3">
                                <p className="mb-1.5 text-[10px] uppercase tracking-wide text-white/35">Owned</p>
                                <ul className="space-y-1.5">
                                    {profile.owned_organizations.map((org) => (
                                        <li key={org.id} className="flex justify-between gap-2 text-xs">
                                            <span className="text-white/80">{org.name}</span>
                                            <span className="shrink-0 text-white/40">{formatWhen(org.created_at)} · {org.plan}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {profile.memberships?.length > 0 && (
                            <ul className="space-y-1.5">
                                {profile.memberships.map((m) => (
                                    <li key={`${m.org_id}-${m.role}`} className="flex justify-between gap-2 text-xs">
                                        <span className="text-white/80">{m.org_name || m.org_id} <span className="text-white/40">· {m.role}</span></span>
                                        <span className="shrink-0 text-white/40">{formatWhen(m.joined_at)} · {m.status}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </ProfileSection>

                    <ProfileSection title="Pools & bank details (last 4 only)" empty={!profile.pools?.length}>
                        <ul className="divide-y divide-white/[0.05]">
                            {profile.pools.map((pool) => (
                                <li key={pool.pool_id} className="py-2.5 first:pt-0 last:pb-0">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <p className="text-sm text-white/85">{pool.pool_name}</p>
                                            <p className="mt-0.5 text-xs text-white/45">
                                                {pool.org_name || pool.org_id} · {pool.percentage ?? "—"}%
                                                {pool.role_label ? ` · ${pool.role_label}` : ""}
                                                {pool.is_pool_owner ? " · pool owner" : ""}
                                            </p>
                                        </div>
                                        <span className="text-[10px] text-white/35">{formatWhen(pool.created_at)}</span>
                                    </div>
                                    <div className="mt-1.5"><BankSummary bank={pool.bank} /></div>
                                </li>
                            ))}
                        </ul>
                    </ProfileSection>

                    <ProfileSection title="Known IPs & geolocation" empty={!profile.known_ips?.length && !profile.geo_locations?.length}>
                        {profile.known_ips?.length > 0 && (
                            <p className="mb-2 text-xs text-white/50">From audit activity: {profile.known_ips.join(", ")}</p>
                        )}
                        {profile.geo_locations?.length > 0 && (
                            <ul className="space-y-1.5">
                                {profile.geo_locations.map((geo) => (
                                    <li key={`${geo.ip}-${geo.city}`} className="flex justify-between gap-2 text-xs">
                                        <span className="font-mono text-white/70">{geo.ip}</span>
                                        <span className="text-white/45">{[geo.city, geo.region, geo.country].filter(Boolean).join(", ") || "Unknown"} · {geo.request_count} req</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </ProfileSection>

                    <ProfileSection title="Recent API activity (matched IPs)" empty={!profile.ip_activity?.length}>
                        <ul className="max-h-40 space-y-1 overflow-auto">
                            {profile.ip_activity.map((row, idx) => (
                                <li key={`${row.timestamp}-${idx}`} className="flex justify-between gap-2 text-[11px] font-mono text-white/55">
                                    <span className="truncate">{row.method} {row.path}</span>
                                    <span className="shrink-0">{formatWhen(row.timestamp)}</span>
                                </li>
                            ))}
                        </ul>
                    </ProfileSection>

                    <ProfileSection title="Audit activity" empty={!profile.audit_activity?.length}>
                        <ul className="max-h-48 space-y-1.5 overflow-auto">
                            {profile.audit_activity.map((row) => (
                                <li key={row.id} className="text-xs">
                                    <span className="text-white/75">{row.action}</span>
                                    <span className="text-white/40"> · {formatWhen(row.created_at)}</span>
                                    {row.ip && <span className="font-mono text-white/35"> · {row.ip}</span>}
                                </li>
                            ))}
                        </ul>
                    </ProfileSection>

                    <ProfileSection title="Login security" empty={!profile.login_attempts?.length}>
                        <ul className="space-y-1.5">
                            {profile.login_attempts.map((row) => (
                                <li key={row.identifier} className="flex justify-between gap-2 text-xs font-mono text-white/55">
                                    <span className="truncate">{row.identifier}</span>
                                    <span className="shrink-0">{row.count} fails · {formatWhen(row.last_failed)}</span>
                                </li>
                            ))}
                        </ul>
                    </ProfileSection>

                    <ProfileSection title="Recent payouts initiated" empty={!profile.recent_payouts?.length}>
                        <ul className="space-y-1.5">
                            {profile.recent_payouts.map((p) => (
                                <li key={p.id} className="flex justify-between gap-2 text-xs">
                                    <span className="text-white/75">{p.status} · pool {p.pool_id?.slice(0, 8)}…</span>
                                    <span className="font-mono text-white/45">{p.total_amount} {p.currency} · {formatWhen(p.date)}</span>
                                </li>
                            ))}
                        </ul>
                    </ProfileSection>
                </div>
            )}
        </Dialog>
    );
}

export function AdminPoolProfileDialog({ poolId, open, onClose }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!poolId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/pools/${poolId}/profile`);
            setProfile(data);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load pool profile"));
            onClose?.();
        } finally {
            setLoading(false);
        }
    }, [poolId, onClose]);

    useEffect(() => {
        if (open && poolId) {
            setProfile(null);
            load();
        }
    }, [open, poolId, load]);

    if (!open) return null;

    const pool = profile?.pool;
    const org = profile?.organization;

    return (
        <Dialog
            onClose={onClose}
            title={pool?.name || "Pool profile"}
            subtitle={org ? `${org.name} · ${pool?.cadence || "—"} · ${pool?.currency || "—"}` : "Members, bank masks, and activity"}
            testid="admin-pool-profile-dialog"
            size="profile"
        >
            {loading || !profile ? (
                <LoadingBody />
            ) : (
                <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
                    <ProfileSection title="Pool">
                        <dl className="grid gap-2 text-xs sm:grid-cols-2">
                            <div><dt className="text-white/40">Pool ID</dt><dd className="font-mono text-white/75">{pool.id}</dd></div>
                            <div><dt className="text-white/40">Created</dt><dd className="text-white/75">{formatWhen(pool.created_at)}</dd></div>
                            <div><dt className="text-white/40">Status</dt><dd className="capitalize text-white/75">{pool.status || "active"}</dd></div>
                            <div><dt className="text-white/40">Total revenue</dt><dd className="font-mono text-white/75">{pool.total_revenue ?? 0}</dd></div>
                            {pool.description && (
                                <div className="sm:col-span-2"><dt className="text-white/40">Description</dt><dd className="text-white/75">{pool.description}</dd></div>
                            )}
                        </dl>
                    </ProfileSection>

                    <ProfileSection title="Organization">
                        {org ? (
                            <dl className="grid gap-2 text-xs sm:grid-cols-2">
                                <div><dt className="text-white/40">Name</dt><dd className="text-white/75">{org.name}</dd></div>
                                <div><dt className="text-white/40">Plan</dt><dd className="capitalize text-white/75">{org.plan || "free"}</dd></div>
                                <div><dt className="text-white/40">Org ID</dt><dd className="font-mono text-white/75">{org.id}</dd></div>
                                <div><dt className="text-white/40">Created</dt><dd className="text-white/75">{formatWhen(org.created_at)}</dd></div>
                            </dl>
                        ) : (
                            <p className="text-xs text-white/40">Organization not found.</p>
                        )}
                    </ProfileSection>

                    <ProfileSection title="Members" empty={!profile.members?.length}>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[520px] text-left text-xs">
                                <thead className="text-[10px] uppercase tracking-wide text-white/35">
                                    <tr>
                                        <th className="pb-2 pr-3 font-medium">Name</th>
                                        <th className="pb-2 pr-3 font-medium">Email</th>
                                        <th className="pb-2 pr-3 font-medium">Role</th>
                                        <th className="pb-2 pr-3 font-medium">Share</th>
                                        <th className="pb-2 font-medium">Bank (last 4)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/[0.05]">
                                    {profile.members.map((member) => (
                                        <tr key={member.member_id || member.email}>
                                            <td className="py-2 pr-3 text-white/85">
                                                {member.name || "—"}
                                                {member.is_owner && <span className="ml-1 text-[10px] text-[#00BF0D]">owner</span>}
                                            </td>
                                            <td className="py-2 pr-3 font-mono text-white/60">{member.email || "—"}</td>
                                            <td className="py-2 pr-3 text-white/50">{member.role_label || "—"}</td>
                                            <td className="py-2 pr-3 font-mono text-white/60">{member.percentage ?? "—"}%</td>
                                            <td className="py-2"><BankSummary bank={member.bank} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ProfileSection>

                    <ProfileSection title="Recent payouts" empty={!profile.recent_payouts?.length}>
                        <ul className="space-y-2">
                            {profile.recent_payouts.map((payout) => (
                                <li key={payout.id} className="rounded-md border border-white/[0.06] bg-white/[0.015] p-2.5 text-xs">
                                    <div className="flex justify-between gap-2">
                                        <span className="text-white/75">{payout.status} · {formatWhen(payout.date)}</span>
                                        <span className="font-mono text-white/55">{payout.total_amount} {payout.currency}</span>
                                    </div>
                                    {payout.recipients?.length > 0 && (
                                        <ul className="mt-1.5 space-y-0.5 text-white/45">
                                            {payout.recipients.map((r, idx) => (
                                                <li key={idx}>{r.name}: {r.amount}{r.bank_last4 ? ` · ···${r.bank_last4}` : ""}</li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </ProfileSection>

                    <ProfileSection title="Audit activity" empty={!profile.audit_activity?.length}>
                        <ul className="max-h-48 space-y-1.5 overflow-auto">
                            {profile.audit_activity.map((row) => (
                                <li key={row.id} className="text-xs">
                                    <span className="text-white/75">{row.action}</span>
                                    <span className="text-white/40"> · {formatWhen(row.created_at)}</span>
                                    {row.user_email && <span className="text-white/35"> · {row.user_email}</span>}
                                </li>
                            ))}
                        </ul>
                    </ProfileSection>
                </div>
            )}
        </Dialog>
    );
}
